import { useState, useEffect } from 'react';
import imageCache from '../services/imageCache';

interface UseCachedImageOptions {
  fallbackType?: string;
  size?: string;
}

export const useCachedImage = (imagePath: string, options: UseCachedImageOptions = {}) => {
  const { fallbackType = 'default', size = 'w500' } = options;
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!imagePath) {
        if (isMounted) {
          const fallbackUrl = await imageCache.getImageUrl('', size, fallbackType);
          setImageUrl(fallbackUrl);
          setIsLoading(false);
        }
        return;
      }

      try {
        setIsLoading(true);
        setHasError(false);
        
        const url = await imageCache.getImageUrl(imagePath, size, fallbackType);
        
        if (isMounted) {
          setImageUrl(url);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading cached image:', error);
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [imagePath, size, fallbackType]);

  return {
    imageUrl,
    isLoading,
    hasError
  };
};

export default useCachedImage;
